import {Attributes, Avatar, DataStatus} from 'platform/components';
import {Box, Heading, HStack, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';

import {RentalOrderResponseBody, useGetRentalOrderQuery} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {Nullish, parseDate} from 'shared/web';

import {useGetVehicleInformation} from '../../../../../hooks/useGetVehicleInformation';
import {useGetRentalVehicleBasicInformation} from '../hooks/useGetRentalVehicleBasicInformation';

interface RentalVehicleDetailProps {
  rentalOrder?: RentalOrderResponseBody | undefined;
}

export function RentalVehicleDetail(props: RentalVehicleDetailProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data: rentalOrder} = useGetRentalOrderQuery(
    {rentalOrderId: props.rentalOrder?.rentalOrderId ?? ''},
    {skip: isNil(props.rentalOrder?.rentalOrderId)}
  );

  const vehicleId = rentalOrder?.rentalVehicleId;

  const [vehicleRows, areVehicleInformationLoading] = useGetVehicleInformation(vehicleId);
  const [basicInformationRows, areBasicInformationLoading] =
    useGetRentalVehicleBasicInformation(vehicleId);

  const getRentalPeriod = (value: string | Nullish) => {
    if (isNil(value)) {
      return null;
    }

    return formatDateTime('dateTimeShort', parseDate(value));
  };

  return (
    <VStack spacing={4}>
      <Heading size={4}>{i18n.t('general.labels.basicInformation')}</Heading>

      <HStack spacing={3}>
        <Box width="37%" minWidth={41}>
          <HStack spacing={4} align="center">
            <Avatar icon="navigation/arrow_upward" variant="square" />
            <VStack>
              <Text size="xSmall">{i18n.t('page.rentalOrder.labels.rent')}</Text>
              <Heading size={4}>{getRentalPeriod(rentalOrder?.plannedRentalPeriod?.from)}</Heading>
            </VStack>
          </HStack>
        </Box>
        <Box>
          <HStack spacing={4} align="center">
            <Avatar icon="navigation/arrow_downward" variant="square" />
            <VStack>
              <Text size="xSmall">{i18n.t('page.rentalOrder.labels.return')}</Text>
              <Heading size={4}>{getRentalPeriod(rentalOrder?.plannedRentalPeriod?.to)}</Heading>
            </VStack>
          </HStack>
        </Box>
      </HStack>

      <DataStatus isLoading={areBasicInformationLoading} minHeight={16}>
        <Attributes rows={basicInformationRows} size="third" />
      </DataStatus>

      <Heading size={4}>{i18n.t('entity.vehicle.labels.vehicle')}</Heading>

      <DataStatus isLoading={areVehicleInformationLoading} minHeight={99}>
        <Attributes
          rows={vehicleRows}
          size="third"
          data-testid={testIds.rentalOrder.vehicleAndCustomer('vehicleAttributes')}
        />
      </DataStatus>
    </VStack>
  );
}
